import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ChakraProvider } from '@chakra-ui/react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import theme from './theme';
import HomeLanding from './pages/HomeLanding';
import SignInPage from './pages/SignInPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AuthenticatedLanding from './pages/AuthenticatedLanding';
import NotFoundPage from './pages/NotFoundPage';
import UserRoute from './components/UserRoute';
import ResearchPage from './pages/ResearchPage';
import ContactUsPage from './pages/ContactUsPage';
import TermsPage from './pages/TermsPage';
import SmsPolicyPage from './pages/SmsPolicyPage';
import { useAuthStore } from './store/authStore';
import RosterPage from './pages/RosterPage';
import LineupsPage from './pages/LineupsPage';
import DepthChartPage from './pages/DepthChartPage';
import { ChatProvider } from './features/chat';
import { useEffect } from 'react';
import SignUpPage from './pages/SignUpPage';
import MyProfilePage from './pages/MyProfilePage';
import { UserProvider } from './contexts/UserContext';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import CheckoutPage from './pages/CheckoutPage';
import PaymentConfirmationPage from './pages/PaymentConfirmationPage';
import FreeAgents from './pages/FreeAgents';
import WaiverWire from './pages/WaiverWire';
import Transactions from './pages/Transactions';
import Purchases from './pages/Purchases';
import StorePage from './pages/StorePage';
import Trades from './components/Trades';
import { useGlobalNotification } from './hooks/useGlobalNotification';
import InviteLandingPage from './pages/InviteLandingPage';
import HowItWorks from './pages/HowItWorks';
import MessagesPage from './pages/MessagesPage';
import LeagueSchedulePage from './pages/LeagueSchedulePage';
import StandingsPage from './pages/StandingsPage';
import JoinLeaguePage from './pages/JoinLeaguePage';
import StatisticsPage from './pages/StatisticsPage';
import LeagueScoreboardPage from './pages/LeagueScoreboardPage';
import GameScoreDetailsPage from './pages/GameScoreDetailsPage';
import { STRIPE_KEY } from './constants';

const queryClient = new QueryClient();
const stripePromise = loadStripe(STRIPE_KEY);

// Wrapper component for authenticated routes
const AuthenticatedRoutes = ({ children }: { children: React.ReactNode }) => {
  // Initialize global notification polling for authenticated users
  useGlobalNotification();

  return (
    <UserProvider>
      <UserRoute>
        {children}
      </UserRoute>
    </UserProvider>
  );
};

function App() {
  const { isSignedIn, checkAuth } = useAuthStore();

  // Listen for storage changes
  useEffect(() => {
    const handleStorageChange = () => {
      checkAuth();
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [checkAuth]);

  return (
    <DndProvider backend={HTML5Backend}>
      <ChakraProvider
        theme={theme}
        toastOptions={{
          defaultOptions: {
            position: 'bottom',
            duration: 5000,
            isClosable: true,
            variant: 'solid',
            containerStyle: {
              margin: '16px',
              marginBottom: '32px',
            },
          },
        }}
      >
        <Elements stripe={stripePromise}>
          <QueryClientProvider client={queryClient}>
            <ChatProvider>
              <Router>
                <Routes>
                  <Route path="/" element={<HomeLanding />} />
                  <Route path="/signin" element={<SignInPage />} />
                  <Route path="/reset-password" element={<ForgotPasswordPage />} />
                  <Route path="/signup" element={<SignUpPage />} />
                  <Route path="/invite/:shortCode" element={<InviteLandingPage />} />
                  <Route path="/contact" element={isSignedIn ? <AuthenticatedRoutes><ContactUsPage /></AuthenticatedRoutes> : <ContactUsPage />} />
                  <Route path="/terms" element={isSignedIn ? <AuthenticatedRoutes><TermsPage /></AuthenticatedRoutes> : <TermsPage />} />
                  <Route path="/SMS-policy" element={isSignedIn ? <AuthenticatedRoutes><SmsPolicyPage /></AuthenticatedRoutes> : <SmsPolicyPage />} />
                  <Route
                    path="/dashboard"
                    element={
                      <AuthenticatedRoutes>
                        <AuthenticatedLanding />
                      </AuthenticatedRoutes>
                    }
                  />
                  <Route path="/research" element={<AuthenticatedRoutes><ResearchPage /></AuthenticatedRoutes>} />
                  <Route path="/free-agents" element={<AuthenticatedRoutes><FreeAgents /></AuthenticatedRoutes>} />
                  <Route path="/waiver-wire" element={<AuthenticatedRoutes><WaiverWire /></AuthenticatedRoutes>} />
                  <Route path="/transactions" element={<AuthenticatedRoutes><Transactions /></AuthenticatedRoutes>} />
                  <Route path="/purchases" element={<AuthenticatedRoutes><Purchases /></AuthenticatedRoutes>} />
                  <Route path="/store" element={<AuthenticatedRoutes><StorePage /></AuthenticatedRoutes>} />
                  <Route path="/roster" element={<AuthenticatedRoutes><RosterPage /></AuthenticatedRoutes>} />
                  <Route path="/rosters/:teams?" element={<AuthenticatedRoutes><RosterPage /></AuthenticatedRoutes>} />
                  <Route path="/lineups" element={<AuthenticatedRoutes><LineupsPage /></AuthenticatedRoutes>} />
                  <Route path="/depth-chart" element={<AuthenticatedRoutes><DepthChartPage /></AuthenticatedRoutes>} />
                  <Route path="/trades" element={<AuthenticatedRoutes><Trades isMyTrades={false} /></AuthenticatedRoutes>} />
                  <Route path="/my-trades" element={<AuthenticatedRoutes><Trades isMyTrades={true} /></AuthenticatedRoutes>} />
                  <Route path="/my-profile" element={<AuthenticatedRoutes><MyProfilePage /></AuthenticatedRoutes>} />
                  <Route path="/messages" element={<AuthenticatedRoutes><MessagesPage /></AuthenticatedRoutes>} />
                  <Route path="/notifications" element={<AuthenticatedRoutes><MessagesPage /></AuthenticatedRoutes>} />
                  <Route path="/checkout" element={<AuthenticatedRoutes><CheckoutPage /></AuthenticatedRoutes>} />
                  <Route path="/payment-confirmation" element={<AuthenticatedRoutes><PaymentConfirmationPage /></AuthenticatedRoutes>} />
                  <Route path="/about/:tab?" element={<AuthenticatedRoutes><HowItWorks /></AuthenticatedRoutes>} />
                  <Route path="/league-schedule" element={<AuthenticatedRoutes><LeagueSchedulePage /></AuthenticatedRoutes>} />
                  <Route path="/scoreboard" element={<AuthenticatedRoutes><LeagueScoreboardPage /></AuthenticatedRoutes>} />
                  <Route path="/league-scoreboard" element={<AuthenticatedRoutes><LeagueScoreboardPage /></AuthenticatedRoutes>} />
                  <Route path="/league-scoreboard/:teamId" element={<AuthenticatedRoutes><LeagueScoreboardPage /></AuthenticatedRoutes>} />
                  <Route path="/game-score-details/:gameId" element={<AuthenticatedRoutes><GameScoreDetailsPage /></AuthenticatedRoutes>} />
                  <Route path="/standings" element={<AuthenticatedRoutes><StandingsPage /></AuthenticatedRoutes>} />
                  <Route path="/join-league" element={<AuthenticatedRoutes><JoinLeaguePage /></AuthenticatedRoutes>} />
                  <Route path="/league-stats" element={<AuthenticatedRoutes><StatisticsPage /></AuthenticatedRoutes>} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Router>
            </ChatProvider>
          </QueryClientProvider>
        </Elements>
      </ChakraProvider>
    </DndProvider>
  );
}

export default App;
